// Angular Files
import { Injectable, Injector } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';

// Teller Online Files
import { ServicesModule } from 'apps/public-portal/src/app/shared/services/services.module';
import { HELP_ARTICLES } from 'apps/public-portal/src/app/shared/help/help-articles';
import { PublicHelpTag } from 'apps/public-portal/src/app/shared/help/help-tags';
import { PaymentProcessorProvider } from 'apps/public-portal/src/app/payment-integrations';

// Teller Online Library Files
import {
    HelpServiceBase,
    HelpArticle,
    HelpTag,
    HelpConditionContext
} from 'teller-online-libraries/help';
import { TerminologyNameEnumDto } from 'libraries/core/src/lib/api/CoreWebApi';
import { TellerOnlineSiteMetadataService } from 'teller-online-libraries/core';

@Injectable({
    providedIn: ServicesModule
})
export class HelpService extends HelpServiceBase {
    private _context: HelpConditionContext;

    constructor(
        sanitizer: DomSanitizer,
        injector: Injector
    ) {
        super(sanitizer);

        // Because the help service is used in _____ it can't load in other services, so we inject them afterwards to avoid circular dependencies
        const paymentProvider = injector.get(PaymentProcessorProvider);
        const siteMetadataService = injector.get(TellerOnlineSiteMetadataService);

        this._context = {
            tenantName: siteMetadataService.tenant,
            // TODO PROD-276: Re-evaluate if we can no longer assume Credit and E-Check are using the same processor
            processorName: paymentProvider.creditProcessorName,
            isRedirect: paymentProvider.activeCreditProcessor.redirect,
            authenticatedUsersEnabled: siteMetadataService.authenticationEnabled,
            tellerOnlineLiteEnabled: siteMetadataService.tellerOnlineLiteEnabled,
            scheduledPaymentsEnabled: siteMetadataService.scheduledPaymentsEnabled,
            linkedAccountsEnabled: siteMetadataService.linkedAccountsEnabled,
            linkedAccountTerm: siteMetadataService.term(TerminologyNameEnumDto.LinkedAccount)
        };

        this.initializeArticles();
    }

    protected getConditionContext(): HelpConditionContext {
        return this._context;
    }

    protected getArticleDefinitions(): HelpArticle[] {
        return HELP_ARTICLES;
    }

    public determineTagByUrl(currentUrl: string): HelpTag {
        // Match whole route segments of the path only: the query string holds what the user
        // searched for, and search paths end in the tenant's own search type name
        const [first, second] = currentUrl.split(/[?#]/)[0].split('/').filter(Boolean);

        if (first === 'profile') {
            switch (second) {
                case 'linked-accounts':
                    return PublicHelpTag.LinkedAccounts;
                case 'payment-history':
                    return PublicHelpTag.PaymentHistory;
                case 'payment-methods':
                    return PublicHelpTag.PaymentMethods;
                default:
                    return PublicHelpTag.ManageProfile;
            }
        }

        if (first === 'checkout') {
            return second === 'success' ? PublicHelpTag.PaymentHistory : PublicHelpTag.Checkout;
        }

        if (['sign-in', 'sign-up', 'forgot-password', 'force-change-password'].includes(first)) {
            return PublicHelpTag.Authentication;
        }

        return PublicHelpTag.Search;
    }

    public getAvailableTags(): HelpTag[] {
        // Return all tags from the PublicHelpTag enum
        return Object.values(PublicHelpTag);
    }
}

// Re-export types from shared library for backwards compatibility
export { HelpArticle, HelpConditional } from 'teller-online-libraries/help';
// Export public portal specific HelpTag enum
export { PublicHelpTag } from 'apps/public-portal/src/app/shared/help/help-tags';
